import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  SingularDayCategory,
  SingularDayDemandBehavior,
} from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import { z } from 'zod'

/**
 * 特異日登録・編集モーダル共通の Zod schema (spec AC-010/AC-012)。
 *
 * バリデーション:
 *  - name: 必須、 30 文字以内
 *  - category: 必須 (カテゴリ 4 値のいずれか)
 *  - demandBehavior: 必須 (需要扱い 4 値のいずれか)
 *  - singularGroup: 1〜10 の整数。 demand_behavior に関わらず常に保持する
 *    (既定 1、design Note singular-day-special-group.md D2)。 需要扱い
 *    select は demandBehavior + singularGroup を 1 つの proxy 値へ合成した
 *    ものを表示する (`options.ts` の `encodeDemandBehaviorProxy` /
 *    `decodeDemandBehaviorProxy`)。
 *  - areaCodes: 0〜10 個の特定エリア。 空 = 全エリア共通 (AC-07)。
 *    UNSPECIFIED (0) は無効、 重複は不可。 10 個全選択は送信時に空へ正規化
 *    (backend も正規化するため schema では 10 個まで許容する)。
 *
 * 日付は schema に含めない。 登録モーダルは日付入力を別 state で管理し
 * (design AC-004: 日付入力可)、 編集モーダルは日付を readonly 表示する
 * (date は immutable、 identity は surrogate `id`)。 重複 (org, date, area)
 * チェック (AC-08) と楽観ロック衝突は backend の reject を
 * `models/serverError.ts` 経由で表示する。
 */
export const SingularDayFormSchema = z.object({
  name: z
    .string()
    .min(1, '名称を入力してください')
    .refine(
      (v) => Array.from(v).length <= 30,
      '名称は 30文字以内で入力してください',
    ),
  category: z.nativeEnum(SingularDayCategory),
  demandBehavior: z.nativeEnum(SingularDayDemandBehavior),
  singularGroup: z.number().int().min(1).max(10),
  areaCodes: z
    .array(z.nativeEnum(AreaCode))
    .refine(
      (arr) => arr.every((c) => c !== AreaCode.UNSPECIFIED),
      'エリアを選択してください',
    )
    .refine(
      (arr) => new Set(arr).size === arr.length,
      'エリアが重複しています',
    ),
})

export type SingularDayFormValues = z.infer<typeof SingularDayFormSchema>
export type SingularDayFormInput = z.input<typeof SingularDayFormSchema>

/** テスト用の valid な初期値生成ヘルパー。 */
export function makeValidFormValues(
  overrides?: Partial<SingularDayFormValues>,
): SingularDayFormValues {
  return {
    name: 'テスト特異日',
    category: SingularDayCategory.OTHER,
    demandBehavior: SingularDayDemandBehavior.SINGULAR,
    singularGroup: 1,
    areaCodes: [],
    ...overrides,
  }
}

/**
 * 登録モーダルの初期フォーム値 (spec AC-004 / grill Q5 / Q12)。
 *
 * - 需要扱い 既定 = 特異グループ1 (demandBehavior=SINGULAR, singularGroup=1、
 *   design Note singular-day-special-group.md D3/UI「登録モーダル初期値」)
 * - カテゴリ 既定 = その他 (OTHER)。 ただしカテゴリフィルタ選択中は
 *   そのカテゴリを既定にする (`filterCategory` で受け取る)
 * - エリア 既定 = 空 (= 全エリア共通、 AC-07)
 */
export function defaultSingularDayFormValues(
  filterCategory?: SingularDayCategory,
): SingularDayFormValues {
  return {
    name: '',
    category: filterCategory ?? SingularDayCategory.OTHER,
    demandBehavior: SingularDayDemandBehavior.SINGULAR,
    singularGroup: 1,
    areaCodes: [],
  }
}
